/**
 * ONE TAP — bullet impacts, tracers, muzzle flashes and ink decals.
 *
 * A player fires roughly once per life. That single event has to justify the entire
 * economy of the game, so it is deliberately the loudest thing on screen:
 *
 *   1. a MUZZLE FLASH that is a hard four-point star plus a blast collar, not a smear;
 *   2. a FAT TRACER — one decisive line from muzzle to impact, drawn instantly at full
 *      length (it is hitscan; anything that "travels" would be a lie) with a hot bolt
 *      running up it and a screen-space minimum width so it can never thin away to
 *      nothing at range;
 *   3. a BIG GRAPHIC IMPACT: an ink-burst decal painted on the geometry plus debris
 *      chosen by surface — concrete chips and screen-toned dust, metal sparks, glass
 *      shards.
 *
 * All three are built the same way, and it is the way this game has to build everything:
 * a MATTE, ink-outlined, saturated shape carries the read, and an additive core sits
 * inside it and blooms. On white architecture under a blue sky the additive half alone
 * is worth almost nothing — the tracer is drawn twice for exactly this reason, once as
 * an ink-edged line and once as an incandescent core, out of one set of buffers.
 *
 * Surfaces differ in texture, never in hue: it is always ink on white, and only struck
 * metal is allowed to throw amber (see SURFACE_LOOKS).
 *
 * Both pooled batches (decals, tracers) are fixed-capacity ring buffers — three draw
 * calls in total. Nothing here allocates once constructed.
 *
 * Readability rule: impacts are bright and brief, and the decal is always painted at the
 * impact point rather than between the player and the target. No effect in this file is
 * allowed to sit between the crosshair and an enemy for longer than a few frames.
 *
 * Client-only: touches `three`. Never import from src/shared.
 */

import * as THREE from 'three';
import { TEAM_COLORS } from '@shared/constants';
import { SurfaceKind } from '@shared/types';
import {
  applyVfxBlend,
  P_CHIP,
  P_DOT,
  P_INK,
  P_PUFF,
  P_RING,
  P_SHARD,
  P_SPARK,
  P_STAR,
  VFX,
  type ParticleSystem,
} from './particles';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const DECAL_CAPACITY = 96;
/** How long an ink burst stays painted on the wall. Long enough to read the fight's history. */
const DECAL_LIFE = 14;
const DECAL_BIAS = 0.035;

const TRACER_CAPACITY = 24;
const TRACER_LIFE = 0.20;
/** Half-width, metres. 0.12 m across is a deliberately fat, decisive line. */
const TRACER_WIDTH = 0.06;
/**
 * Minimum angular half-width, radians. At a 92 degree vertical FOV one screen pixel is
 * about 0.0019 rad per unit of distance, so this floors the tracer at roughly three
 * pixels across however far away it is drawn. A shot must never thin into nothing.
 */
const TRACER_MIN_ANGULAR = 0.0030;
/** Fraction of the tracer's half-width given over to its ink edge. */
const TRACER_INK_EDGE = 0.30;
/** How hard the incandescent pass burns above the matte line it sits on. */
const TRACER_HOT_GAIN = 4.0;

/** Quad corner offsets in the decal's tangent frame. */
const DECAL_CORNERS = [-1, -1, 1, -1, 1, 1, -1, 1] as const;

/**
 * Local-player effects are anchored roughly a metre from the lens, so a shape sized for
 * the world subtends an enormous angle and can blanket the crosshair. Everything the
 * local player's own muzzle emits is scaled by this and pushed further down the barrel.
 * READABILITY BEATS SPECTACLE: your own gun is never allowed to hide your own shot.
 */
const LOCAL_MUZZLE_SCALE = 0.55;
const LOCAL_MUZZLE_PUSH = 0.55;

/**
 * THE ANGULAR CAP ON SOLID SHAPES.
 *
 * The headshot is authored to be seen from across the atrium: a 4.6 m opaque star and a
 * 3.2 m incandescent one inside it. Land the same shot at three metres — which is most
 * of them — and that is a hundred-and-thirty-degree amber wipe over the frame for a fifth
 * of a second, on the exact beat the player needs to find the rest of the enemy team.
 * DESIGN.md is unambiguous that this is a bug and not a feature.
 *
 * So every FILLED shape in this file is clamped to subtend at most this fraction of its
 * distance from the local player's eye, which at the base 92 degree FOV is about a third
 * of the frame height:
 *
 *   size <= dist * SOLID_MAX_ANGULAR  =>  2 * atan( 0.31 ) = 34 degrees
 *
 * Rings, debris, sparks and dust are all exempt: they are hollow or granular, you see the
 * fight straight through them, and thinning them at close range would cost readability
 * instead of buying it. Beyond about eight metres the cap never engages at all, so the
 * long-range read this game depends on is completely unchanged.
 */
const SOLID_MAX_ANGULAR = 0.62;
/** Below this the effect is effectively at the lens; clamping past it just deletes it. */
const SOLID_MIN_DIST = 0.6;

// ---------------------------------------------------------------------------
// Shared GLSL
// ---------------------------------------------------------------------------

const HASH_GLSL = /* glsl */ `
float hash11( float p ) {

	p = fract( p * 0.1031 );
	p *= p + 33.33;
	p *= p + p;
	return fract( p );

}
`;

// ---------------------------------------------------------------------------
// Ink decals
// ---------------------------------------------------------------------------

const DECAL_VERT = /* glsl */ `
attribute vec2 aUv;
attribute vec3 aCol;
attribute vec3 aHot;
attribute vec3 aParam;   // seed, birth, life

uniform float uTime;

varying vec2 vUv;
varying vec3 vCol;
varying vec3 vHot;
varying float vSeed;
varying float vFade;
varying float vGrow;

void main() {

	float t = uTime - aParam.y;
	float life = max( aParam.z, 1e-3 );

	if ( t < 0.0 || t >= life ) {

		vUv = vec2( 0.0 );
		vCol = vec3( 0.0 );
		vHot = vec3( 0.0 );
		vSeed = 0.0;
		vFade = 0.0;
		vGrow = 0.0;
		gl_Position = vec4( 0.0, 0.0, 2.0, 1.0 );
		return;

	}

	float u = t / life;

	vUv = aUv;
	vCol = aCol;
	vHot = aHot;
	vSeed = aParam.x;
	// Ink hangs at full strength for most of its life and then dries out fast, so a wall
	// records the fight instead of fading into a permanent grey wash.
	vFade = 1.0 - smoothstep( 0.62, 1.0, u );
	// The splat "lands": it snaps open over 50 ms, in absolute time, not life fraction.
	vGrow = smoothstep( 0.0, 0.05, t );

	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}
`;

const DECAL_FRAG = /* glsl */ `
varying vec2 vUv;
varying vec3 vCol;
varying vec3 vHot;
varying float vSeed;
varying float vFade;
varying float vGrow;

${HASH_GLSL}

/** Irregular splat plus thrown droplets. Pure maths — no texture, no atlas, no bleed. */
float splat( vec2 p, float seed, float scale ) {

	if ( scale <= 1e-3 ) return 0.0;
	p /= scale;

	float r = length( p );
	float a = atan( p.y, p.x );

	float edge = 0.50
		+ 0.135 * sin( a * 3.0 + seed * 13.0 )
		+ 0.085 * sin( a * 6.0 - seed * 7.0 )
		+ 0.050 * sin( a * 11.0 + seed * 3.0 )
		+ 0.028 * sin( a * 19.0 - seed * 21.0 );

	float w = fwidth( r ) + 0.006;
	float m = 1.0 - smoothstep( edge - w, edge + w, r );

	for ( int i = 0; i < 5; i ++ ) {
		float fi = float( i );
		float ang = hash11( seed * 3.7 + fi ) * 6.2831853;
		float dr = 0.52 + 0.36 * hash11( seed * 9.1 + fi * 2.3 );
		vec2 dp = vec2( cos( ang ), sin( ang ) ) * dr;
		float ds = 0.035 + 0.085 * hash11( seed * 4.4 + fi * 5.1 );
		float d = length( p - dp );
		float ww = fwidth( d ) + 0.006;
		m = max( m, 1.0 - smoothstep( ds - ww, ds + ww, d ) );
	}

	return m;

}

void main() {

	float m = splat( vUv, vSeed, vGrow );
	if ( m <= 0.003 ) discard;

	// A bright bite through the middle: the crater reads as a hole punched in the ink.
	float r = length( vUv ) / max( vGrow, 1e-3 );
	float w = fwidth( r ) + 0.01;
	float core = 1.0 - smoothstep( 0.16 - w, 0.16 + w, r );

	vec3 col = mix( vCol, vHot, core );
	float a = m * vFade;

	gl_FragColor = vec4( col * a, a );

}
`;

const _n = new THREE.Vector3();
const _u = new THREE.Vector3();
const _v = new THREE.Vector3();
const _color = new THREE.Color();

class DecalPool {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly hot: Float32Array;
  private readonly param: Float32Array;
  private readonly attrs: THREE.BufferAttribute[];
  private readonly capacity: number;
  private cursor = 0;
  private dirtyLo = Number.MAX_SAFE_INTEGER;
  private dirtyHi = -1;

  constructor(capacity: number, renderOrder: number) {
    this.capacity = capacity;
    const verts = capacity * 4;
    this.pos = new Float32Array(verts * 3);
    this.col = new Float32Array(verts * 3);
    this.hot = new Float32Array(verts * 3);
    this.param = new Float32Array(verts * 3);
    const uv = new Float32Array(verts * 2);
    for (let i = 0; i < capacity; i++) {
      const b = i * 8;
      uv[b] = -1;
      uv[b + 1] = -1;
      uv[b + 2] = 1;
      uv[b + 3] = -1;
      uv[b + 4] = 1;
      uv[b + 5] = 1;
      uv[b + 6] = -1;
      uv[b + 7] = 1;
    }
    // Everything starts expired: life 0 means the vertex stage clips it.
    for (let i = 0; i < verts; i++) this.param[i * 3 + 2] = 0;

    const index = capacity * 4 > 65535 ? new Uint32Array(capacity * 6) : new Uint16Array(capacity * 6);
    let w = 0;
    for (let i = 0; i < capacity; i++) {
      const a = i * 4;
      index[w++] = a;
      index[w++] = a + 1;
      index[w++] = a + 2;
      index[w++] = a;
      index[w++] = a + 2;
      index[w++] = a + 3;
    }

    const geo = new THREE.BufferGeometry();
    geo.name = 'vfx-decals';
    const mk = (data: Float32Array, size: number): THREE.BufferAttribute => {
      const at = new THREE.BufferAttribute(data, size);
      at.setUsage(THREE.DynamicDrawUsage);
      return at;
    };
    const aPos = mk(this.pos, 3);
    const aCol = mk(this.col, 3);
    const aHot = mk(this.hot, 3);
    const aParam = mk(this.param, 3);
    geo.setAttribute('position', aPos);
    geo.setAttribute('aUv', new THREE.BufferAttribute(uv, 2));
    geo.setAttribute('aCol', aCol);
    geo.setAttribute('aHot', aHot);
    geo.setAttribute('aParam', aParam);
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.attrs = [aPos, aCol, aHot, aParam];

    const mat = new THREE.ShaderMaterial({
      name: 'vfx-decals',
      uniforms: { uTime: { value: 0 } },
      vertexShader: DECAL_VERT,
      fragmentShader: DECAL_FRAG,
      side: THREE.DoubleSide,
      depthTest: true,
      toneMapped: false,
    });
    applyVfxBlend(mat, 'over');
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -4;
    mat.polygonOffsetUnits = -8;

    this.geometry = geo;
    this.material = mat;
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = 'vfx-decals';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = renderOrder;
  }

  setTime(t: number): void {
    this.material.uniforms.uTime.value = t;
  }

  place(
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    size: number,
    body: number,
    hot: number,
    seed: number,
    now: number,
    life: number,
    rotation: number,
  ): void {
    _n.set(nx, ny, nz);
    if (_n.lengthSq() < 1e-8) _n.set(0, 1, 0);
    _n.normalize();

    // Stable tangent frame; pick the world axis least aligned with the normal.
    if (Math.abs(_n.y) > 0.94) _u.set(1, 0, 0);
    else _u.set(0, 1, 0);
    _v.crossVectors(_u, _n).normalize();
    _u.crossVectors(_n, _v).normalize();

    const cs = Math.cos(rotation);
    const sn = Math.sin(rotation);
    const ux = _u.x * cs + _v.x * sn;
    const uy = _u.y * cs + _v.y * sn;
    const uz = _u.z * cs + _v.z * sn;
    const vx = _v.x * cs - _u.x * sn;
    const vy = _v.y * cs - _u.y * sn;
    const vz = _v.z * cs - _u.z * sn;

    const h = size * 0.5;
    const cx = x + _n.x * DECAL_BIAS;
    const cy = y + _n.y * DECAL_BIAS;
    const cz = z + _n.z * DECAL_BIAS;

    const i = this.cursor;
    this.cursor = this.cursor + 1 >= this.capacity ? 0 : this.cursor + 1;
    const base = i * 4;

    _color.setHex(body, THREE.SRGBColorSpace);
    const br = _color.r;
    const bg = _color.g;
    const bb = _color.b;
    _color.setHex(hot, THREE.SRGBColorSpace);
    const hr = _color.r;
    const hg = _color.g;
    const hb = _color.b;

    for (let c = 0; c < 4; c++) {
      const su = DECAL_CORNERS[c * 2];
      const sv = DECAL_CORNERS[c * 2 + 1];
      const o = (base + c) * 3;
      this.pos[o] = cx + vx * h * sv + ux * h * su;
      this.pos[o + 1] = cy + vy * h * sv + uy * h * su;
      this.pos[o + 2] = cz + vz * h * sv + uz * h * su;
      this.col[o] = br;
      this.col[o + 1] = bg;
      this.col[o + 2] = bb;
      this.hot[o] = hr;
      this.hot[o + 1] = hg;
      this.hot[o + 2] = hb;
      this.param[o] = seed;
      this.param[o + 1] = now;
      this.param[o + 2] = life;
    }

    if (base < this.dirtyLo) this.dirtyLo = base;
    if (base + 3 > this.dirtyHi) this.dirtyHi = base + 3;
  }

  flush(): void {
    if (this.dirtyHi < this.dirtyLo) return;
    const lo = this.dirtyLo;
    const count = this.dirtyHi - lo + 1;
    for (let a = 0; a < this.attrs.length; a++) {
      const attr = this.attrs[a];
      attr.clearUpdateRanges();
      attr.addUpdateRange(lo * attr.itemSize, count * attr.itemSize);
      attr.needsUpdate = true;
    }
    this.dirtyLo = Number.MAX_SAFE_INTEGER;
    this.dirtyHi = -1;
  }

  clear(): void {
    for (let i = 0; i < this.param.length; i += 3) this.param[i + 2] = 0;
    this.dirtyLo = 0;
    this.dirtyHi = this.param.length / 3 - 1;
    this.cursor = 0;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Tracers
// ---------------------------------------------------------------------------

const TRACER_VERT = /* glsl */ `
attribute vec3 aA;
attribute vec3 aB;
attribute vec2 aEdge;   // across (-1/+1), along (0/1)
attribute vec3 aCol;
attribute vec3 aHot;
attribute vec3 aParam;  // birth, life, width

uniform float uTime;

varying vec3 vCol;
varying vec3 vHot;
varying float vAcross;
varying float vAlong;
varying float vFade;
varying float vU;

void main() {

	float life = max( aParam.y, 1e-4 );
	float t = uTime - aParam.x;

	if ( t < 0.0 || t >= life ) {

		vCol = vec3( 0.0 );
		vHot = vec3( 0.0 );
		vAcross = 0.0;
		vAlong = 0.0;
		vFade = 0.0;
		vU = 0.0;
		gl_Position = vec4( 0.0, 0.0, 2.0, 1.0 );
		return;

	}

	float u = t / life;

	vec3 wp = mix( aA, aB, aEdge.y );
	vec4 mv = modelViewMatrix * vec4( wp, 1.0 );

	vec3 seg = ( modelViewMatrix * vec4( aB - aA, 0.0 ) ).xyz;
	float sl = length( seg );
	vec3 dir = sl > 1e-5 ? seg / sl : vec3( 0.0, 1.0, 0.0 );

	vec3 vd = normalize( - mv.xyz );
	vec3 s = cross( dir, vd );
	float ll = length( s );
	s = ll > 1e-4 ? s / ll : normalize( cross( dir, vec3( 0.0, 0.0, 1.0 ) ) + vec3( 1e-3, 0.0, 0.0 ) );

	// Hold the fat line for the first fifth of its life, then draw it down to a hairline.
	float thin = 1.0 - smoothstep( 0.20, 1.0, u );
	// Never let distance reduce the shot to a sub-pixel flicker: enforce a minimum
	// angular width, which is what makes a cross-map tracer still read as a line.
	float dist = length( mv.xyz );
	float width = max( aParam.z * thin, dist * ${TRACER_MIN_ANGULAR.toFixed(5)} );

	mv.xyz += s * ( aEdge.x * width );

	vCol = aCol;
	vHot = aHot;
	vAcross = aEdge.x;
	vAlong = aEdge.y;
	vFade = 1.0 - smoothstep( 0.55, 1.0, u );
	vU = u;

	gl_Position = projectionMatrix * mv;

}
`;

const TRACER_FRAG = /* glsl */ `
uniform float uBloom;
uniform vec3 uInk;
uniform float uEdge;
uniform float uHotGain;

varying vec3 vCol;
varying vec3 vHot;
varying float vAcross;
varying float vAlong;
varying float vFade;
varying float vU;

void main() {

	float across = abs( vAcross );
	float w = fwidth( across ) + 0.02;
	float body = 1.0 - smoothstep( 1.0 - w, 1.0, across );
	float core = 1.0 - smoothstep( 0.24, 0.46, across );

	// A hot bolt sprints from muzzle to impact over the first few frames. The line is
	// already fully drawn — this only says which way the shot went.
	float head = smoothstep( 0.0, 0.28, vU );
	float bolt = exp( - 14.0 * abs( vAlong - head ) ) * ( 1.0 - smoothstep( 0.3, 0.6, vU ) );

	#ifdef TRACER_GLOW

		// The incandescent pass: the core stripe only, and it blooms. It is drawn ON TOP
		// of the matte line below, which is what gives the glow something to sit on. On
		// its own, over a white wall, this pass is very nearly nothing.
		float hot = clamp( core + bolt, 0.0, 1.0 );
		float a = body * vFade * hot;
		if ( a <= 0.003 ) discard;
		gl_FragColor = vec4( vHot * uHotGain * a, uBloom * a );

	#else

		// The readable pass: an opaque team-coloured line inside an ink edge. This is the
		// half of the tracer that survives being drawn across white plaster and blue sky,
		// and therefore the half that actually tells you where the shot came from.
		float inner = 1.0 - smoothstep( 1.0 - uEdge - w, 1.0 - uEdge, across );
		vec3 col = mix( uInk, vCol, inner );
		col = mix( col, vHot, clamp( core * 0.7 + bolt, 0.0, 1.0 ) );
		float a = body * vFade;
		if ( a <= 0.003 ) discard;
		gl_FragColor = vec4( col * a, a );

	#endif

}
`;

class TracerPool {
  /** The drawn line. Matte, ink-edged, readable on anything. */
  readonly mesh: THREE.Mesh;
  /** The incandescent core, drawn on top of it. Shares the same geometry and buffers. */
  readonly glowMesh: THREE.Mesh;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly glowMaterial: THREE.ShaderMaterial;
  private readonly a: Float32Array;
  private readonly b: Float32Array;
  private readonly col: Float32Array;
  private readonly hot: Float32Array;
  private readonly param: Float32Array;
  private readonly attrs: THREE.BufferAttribute[];
  private readonly capacity: number;
  private cursor = 0;
  private dirtyLo = Number.MAX_SAFE_INTEGER;
  private dirtyHi = -1;

  constructor(capacity: number, renderOrder: number) {
    this.capacity = capacity;
    const verts = capacity * 4;
    this.a = new Float32Array(verts * 3);
    this.b = new Float32Array(verts * 3);
    this.col = new Float32Array(verts * 3);
    this.hot = new Float32Array(verts * 3);
    this.param = new Float32Array(verts * 3);
    const edge = new Float32Array(verts * 2);
    const position = new Float32Array(verts * 3);
    for (let i = 0; i < capacity; i++) {
      const e = i * 8;
      edge[e] = -1;
      edge[e + 1] = 0;
      edge[e + 2] = 1;
      edge[e + 3] = 0;
      edge[e + 4] = 1;
      edge[e + 5] = 1;
      edge[e + 6] = -1;
      edge[e + 7] = 1;
    }

    const index = new Uint16Array(capacity * 6);
    let w = 0;
    for (let i = 0; i < capacity; i++) {
      const base = i * 4;
      index[w++] = base;
      index[w++] = base + 1;
      index[w++] = base + 2;
      index[w++] = base;
      index[w++] = base + 2;
      index[w++] = base + 3;
    }

    const geo = new THREE.BufferGeometry();
    geo.name = 'vfx-tracers';
    const mk = (data: Float32Array, size: number): THREE.BufferAttribute => {
      const at = new THREE.BufferAttribute(data, size);
      at.setUsage(THREE.DynamicDrawUsage);
      return at;
    };
    const aA = mk(this.a, 3);
    const aB = mk(this.b, 3);
    const aCol = mk(this.col, 3);
    const aHot = mk(this.hot, 3);
    const aParam = mk(this.param, 3);
    geo.setAttribute('position', new THREE.BufferAttribute(position, 3));
    geo.setAttribute('aA', aA);
    geo.setAttribute('aB', aB);
    geo.setAttribute('aEdge', new THREE.BufferAttribute(edge, 2));
    geo.setAttribute('aCol', aCol);
    geo.setAttribute('aHot', aHot);
    geo.setAttribute('aParam', aParam);
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.attrs = [aA, aB, aCol, aHot, aParam];

    const makeMat = (glow: boolean): THREE.ShaderMaterial => {
      const m = new THREE.ShaderMaterial({
        name: glow ? 'vfx-tracers-glow' : 'vfx-tracers',
        defines: glow ? { TRACER_GLOW: '' } : {},
        uniforms: {
          uTime: { value: 0 },
          uBloom: { value: 1 },
          uInk: { value: new THREE.Color().setHex(VFX.ink, THREE.SRGBColorSpace) },
          uEdge: { value: TRACER_INK_EDGE },
          uHotGain: { value: TRACER_HOT_GAIN },
        },
        vertexShader: TRACER_VERT,
        fragmentShader: TRACER_FRAG,
        side: THREE.DoubleSide,
        depthTest: true,
        toneMapped: false,
      });
      applyVfxBlend(m, glow ? 'add' : 'over');
      return m;
    };

    const mat = makeMat(false);
    const glowMat = makeMat(true);

    this.geometry = geo;
    this.material = mat;
    this.glowMaterial = glowMat;

    const mkMesh = (m: THREE.ShaderMaterial, order: number): THREE.Mesh => {
      const mesh = new THREE.Mesh(geo, m);
      mesh.name = m.name;
      mesh.frustumCulled = false;
      mesh.matrixAutoUpdate = false;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = order;
      return mesh;
    };
    // Two draw calls over one set of buffers: the ink line, then the glow on top of it.
    this.mesh = mkMesh(mat, renderOrder);
    this.glowMesh = mkMesh(glowMat, renderOrder + 1);
  }

  setTime(t: number): void {
    this.material.uniforms.uTime.value = t;
    this.glowMaterial.uniforms.uTime.value = t;
  }

  fire(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    body: number,
    hot: number,
    width: number,
    life: number,
    now: number,
  ): void {
    const i = this.cursor;
    this.cursor = this.cursor + 1 >= this.capacity ? 0 : this.cursor + 1;
    const base = i * 4;

    _color.setHex(body, THREE.SRGBColorSpace);
    const br = _color.r;
    const bg = _color.g;
    const bb = _color.b;
    _color.setHex(hot, THREE.SRGBColorSpace);
    const hr = _color.r;
    const hg = _color.g;
    const hb = _color.b;

    for (let c = 0; c < 4; c++) {
      const o = (base + c) * 3;
      this.a[o] = ax;
      this.a[o + 1] = ay;
      this.a[o + 2] = az;
      this.b[o] = bx;
      this.b[o + 1] = by;
      this.b[o + 2] = bz;
      // Raw colours: the matte pass wants them at face value, and the glow pass applies
      // its own gain. Baking an intensity in here would blow the matte line out to white.
      this.col[o] = br;
      this.col[o + 1] = bg;
      this.col[o + 2] = bb;
      this.hot[o] = hr;
      this.hot[o + 1] = hg;
      this.hot[o + 2] = hb;
      this.param[o] = now;
      this.param[o + 1] = life;
      this.param[o + 2] = width;
    }

    if (base < this.dirtyLo) this.dirtyLo = base;
    if (base + 3 > this.dirtyHi) this.dirtyHi = base + 3;
  }

  flush(): void {
    if (this.dirtyHi < this.dirtyLo) return;
    const lo = this.dirtyLo;
    const count = this.dirtyHi - lo + 1;
    for (let a = 0; a < this.attrs.length; a++) {
      const attr = this.attrs[a];
      attr.clearUpdateRanges();
      attr.addUpdateRange(lo * attr.itemSize, count * attr.itemSize);
      attr.needsUpdate = true;
    }
    this.dirtyLo = Number.MAX_SAFE_INTEGER;
    this.dirtyHi = -1;
  }

  clear(): void {
    for (let i = 0; i < this.param.length; i += 3) this.param[i + 1] = 0;
    this.dirtyLo = 0;
    this.dirtyHi = this.param.length / 3 - 1;
    this.cursor = 0;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.glowMaterial.dispose();
  }
}

// ---------------------------------------------------------------------------
// Surface response table
// ---------------------------------------------------------------------------

interface SurfaceLook {
  /** Ink colour of the impact decal. */
  decal: number;
  /** Bright colour punched through the decal's centre. */
  decalHot: number;
  /** Debris tint. */
  debris: number;
  /** Spark tint. */
  spark: number;
  /** Dust tint; 0 disables the dust puff. */
  dust: number;
  /** Debris shape. */
  shape: number;
  /** Relative spark count multiplier. */
  sparkiness: number;
  /** Relative chip count multiplier. */
  chippiness: number;
  /** Base decal diameter, metres. */
  decalSize: number;
}

/**
 * Surfaces differ in TEXTURE, not in hue.
 *
 * Every impact in this game is ink on white: a slate splat, slate debris, cool dust.
 * What changes between concrete and glass is how much of each and what shape it breaks
 * into — a lot of dust and chips versus a lot of shards and very little dust. The one
 * exception is the spark tint on struck metal, which is genuinely incandescent and is
 * the only place amber is allowed outside the chamber.
 *
 * The previous table had a cyan glass decal, a jade neon decal, sun-yellow neon debris
 * and azure neon sparks in it. Four accent hues, spent on the question "what did the
 * bullet hit", which is the least important thing on screen at that moment.
 */
const SURFACE_LOOKS: Record<number, SurfaceLook> = {
  [SurfaceKind.Concrete]: {
    decal: VFX.ink,
    decalHot: VFX.debris,
    debris: VFX.debris,
    spark: VFX.dust,
    dust: VFX.dust,
    shape: P_CHIP,
    sparkiness: 0.35,
    chippiness: 1.4,
    decalSize: 0.85,
  },
  [SurfaceKind.Metal]: {
    decal: VFX.ink,
    decalHot: VFX.hot,
    debris: VFX.debris,
    spark: VFX.hot,
    dust: 0,
    shape: P_CHIP,
    sparkiness: 1.7,
    chippiness: 0.7,
    decalSize: 0.62,
  },
  [SurfaceKind.Grate]: {
    decal: VFX.ink,
    decalHot: VFX.hot,
    debris: VFX.debris,
    spark: VFX.hot,
    dust: 0,
    shape: P_SHARD,
    sparkiness: 2.1,
    chippiness: 0.4,
    decalSize: 0.42,
  },
  [SurfaceKind.Glass]: {
    // Glass is the one surface whose ink is LIGHTER than the wall behind it would be —
    // a frosted starred crater rather than a hole. It still carries an ink rim, so it
    // reads on a curtain wall with sky behind it.
    decal: VFX.dust,
    decalHot: VFX.ink,
    debris: VFX.dust,
    spark: VFX.core,
    dust: 0,
    shape: P_SHARD,
    sparkiness: 1.1,
    chippiness: 2.2,
    decalSize: 0.95,
  },
  [SurfaceKind.Neon]: {
    decal: VFX.ink,
    decalHot: VFX.hot,
    debris: VFX.debris,
    spark: VFX.hot,
    dust: 0,
    shape: P_SHARD,
    sparkiness: 2.4,
    chippiness: 1.5,
    decalSize: 0.7,
  },
};

const DEFAULT_LOOK = SURFACE_LOOKS[SurfaceKind.Concrete];

function lookFor(s: SurfaceKind): SurfaceLook {
  return SURFACE_LOOKS[s] ?? DEFAULT_LOOK;
}

// ---------------------------------------------------------------------------
// ImpactSystem
// ---------------------------------------------------------------------------

export class ImpactSystem {
  private readonly scene: THREE.Scene;
  private readonly particles: ParticleSystem;
  private readonly decals: DecalPool;
  private readonly tracers: TracerPool;
  private time = 0;
  private disposed = false;

  private viewX = 0;
  private viewY = 0;
  private viewZ = 0;
  private hasViewer = false;

  constructor(scene: THREE.Scene, particles: ParticleSystem, renderOrder = 8) {
    this.scene = scene;
    this.particles = particles;
    this.decals = new DecalPool(DECAL_CAPACITY, renderOrder);
    this.tracers = new TracerPool(TRACER_CAPACITY, renderOrder + 6);
    scene.add(this.decals.mesh);
    scene.add(this.tracers.mesh);
    scene.add(this.tracers.glowMesh);
  }

  /**
   * Where the local player's eye is. Pushed in by {@link VfxSystem} every frame; drives
   * the solid-shape cap below. Until it is called the cap is simply inactive, so this
   * system still works standalone.
   */
  setViewer(x: number, y: number, z: number): void {
    this.viewX = x;
    this.viewY = y;
    this.viewZ = z;
    this.hasViewer = true;
  }

  /**
   * Scale factor that keeps a solid shape of peak size `peak` inside SOLID_MAX_ANGULAR.
   * Returns exactly 1 when the shape is already safe, which is the common case — so the
   * `size0 -> size1` animation is untouched at any normal engagement distance.
   */
  private solidScale(peak: number, x: number, y: number, z: number): number {
    if (!this.hasViewer || peak <= 1e-4) return 1;
    const dx = x - this.viewX;
    const dy = y - this.viewY;
    const dz = z - this.viewZ;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const cap = (dist > SOLID_MIN_DIST ? dist : SOLID_MIN_DIST) * SOLID_MAX_ANGULAR;
    return cap >= peak ? 1 : cap / peak;
  }

  // -------------------------------------------------------------------------
  // The shot
  // -------------------------------------------------------------------------

  /** The fat decisive line. `team` tints the outer band; the core is always incandescent. */
  tracer(
    ox: number,
    oy: number,
    oz: number,
    hx: number,
    hy: number,
    hz: number,
    team: number,
    headshot: boolean,
  ): void {
    const body = TEAM_COLORS[team === 1 ? 1 : 0];
    this.tracers.fire(
      ox,
      oy,
      oz,
      hx,
      hy,
      hz,
      body,
      headshot ? VFX.core : VFX.hot,
      TRACER_WIDTH * (headshot ? 1.35 : 1),
      TRACER_LIFE,
      this.time,
    );
  }

  /**
   * Hard four-point star plus a blast collar and a jet of sparks along the barrel.
   *
   * The order matters: a MATTE amber collar and a matte smoke body go down first, then
   * the incandescent star on top of them. That is DESIGN.md 4's rule applied to the
   * flash itself — a pale glow on a pale world is invisible, so the glow gets a body.
   *
   * `local` shrinks the whole thing and pushes it down the barrel. The local muzzle sits
   * about a metre from the lens, where a 1.35 m star is most of the screen.
   */
  muzzleFlash(
    x: number,
    y: number,
    z: number,
    dx: number,
    dy: number,
    dz: number,
    team: number,
    local = false,
  ): void {
    const ps = this.particles;
    const team0 = TEAM_COLORS[team === 1 ? 1 : 0];
    const k = local ? LOCAL_MUZZLE_SCALE : 1;
    const push = local ? LOCAL_MUZZLE_PUSH : 0;
    const ox = x + dx * push;
    const oy = y + dy * push;
    const oz = z + dz * push;

    // --- the body: a matte amber collar. This is the part that reads against a white
    //     wall, and the part the star below has something to burn against.
    let d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = ox + dx * 0.12;
    d.py = oy + dy * 0.12;
    d.pz = oz + dz * 0.12;
    d.life = 0.13;
    d.lifeVar = 0;
    d.size0 = 0.16 * k;
    d.size1 = 1.15 * k;
    d.sizeVar = 0;
    d.color0 = VFX.hot;
    d.color1 = team0;
    d.drag = 5;
    d.priority = 3;
    ps.emit();

    // --- propellant smoke, matte and cool, so the flash has mass.
    d = ps.begin(P_PUFF, false);
    d.count = local ? 2 : 4;
    d.px = ox + dx * 0.2;
    d.py = oy + dy * 0.2;
    d.pz = oz + dz * 0.2;
    d.posJitter = 0.1;
    d.vx = dx * 3.2;
    d.vy = dy * 3.2 + 0.6;
    d.vz = dz * 3.2;
    d.spread = 0.7;
    d.speedVar = 0.5;
    d.gravity = 1.2;
    d.drag = 3.4;
    d.life = 0.55;
    d.lifeVar = 0.4;
    d.size0 = 0.22 * k;
    d.size1 = 0.95 * k;
    d.color0 = VFX.dust;
    d.color1 = VFX.ink;
    d.spinRate = 1.6;
    d.spinVar = 2;
    d.priority = 1;
    ps.emit();

    // --- and only now the light.
    const flashK = k * this.solidScale(1.35 * k, ox, oy, oz);
    d = ps.begin(P_STAR, true);
    d.count = 1;
    d.px = ox;
    d.py = oy;
    d.pz = oz;
    d.life = 0.075;
    d.lifeVar = 0;
    d.size0 = 0.55 * flashK;
    d.size1 = 1.35 * flashK;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 7;
    d.intensity1 = 2.2;
    d.bloom = 1;
    d.spin = ps.random() * Math.PI;
    d.spinRate = 6;
    d.drag = 6;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_SPARK, true);
    d.count = 16;
    d.px = ox;
    d.py = oy;
    d.pz = oz;
    d.posJitter = 0.05;
    d.vx = dx * 13;
    d.vy = dy * 13;
    d.vz = dz * 13;
    d.spread = 0.38;
    d.speedVar = 0.6;
    d.gravity = -9;
    d.drag = 7;
    d.life = 0.16;
    d.lifeVar = 0.5;
    d.size0 = 0.16 * k;
    d.size1 = 0.01;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 5;
    d.intensity1 = 1.2;
    d.bloom = 1;
    d.stretch = 1.4;
    d.priority = 2;
    ps.emit();
  }

  /**
   * A round striking world geometry: ink decal plus surface-appropriate debris.
   * `nx..nz` is the outward surface normal (the shot direction reversed is a fine
   * approximation when a true normal is not available).
   */
  worldImpact(
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    surface: SurfaceKind,
    scale = 1,
  ): void {
    const ps = this.particles;
    const look = lookFor(surface);

    this.decals.place(
      x,
      y,
      z,
      nx,
      ny,
      nz,
      look.decalSize * scale,
      look.decal,
      look.decalHot,
      ps.random(),
      this.time,
      DECAL_LIFE,
      ps.random() * Math.PI * 2,
    );

    // --- the graphic at the point of contact. MATTE ink ring first: a hard drawn
    //     shock front on the surface, which is what survives being painted onto a
    //     sunlit white wall. Then the flash on top of it.
    let d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = x + nx * 0.04;
    d.py = y + ny * 0.04;
    d.pz = z + nz * 0.04;
    d.life = 0.22;
    d.lifeVar = 0;
    d.size0 = 0.2 * scale;
    d.size1 = 1.9 * scale;
    d.sizeVar = 0;
    d.color0 = VFX.ink;
    d.color1 = VFX.dust;
    d.drag = 4;
    d.priority = 2;
    ps.emit();

    const hitK = scale * this.solidScale(1.1 * scale, x, y, z);
    d = ps.begin(P_STAR, true);
    d.count = 1;
    d.px = x + nx * 0.05;
    d.py = y + ny * 0.05;
    d.pz = z + nz * 0.05;
    d.life = 0.09;
    d.lifeVar = 0;
    d.size0 = 0.25 * hitK;
    d.size1 = 1.1 * hitK;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = look.spark;
    d.intensity0 = 6;
    d.intensity1 = 1.4;
    d.bloom = 1;
    d.spin = ps.random() * Math.PI;
    d.drag = 6;
    d.priority = 3;
    ps.emit();

    // --- sparks
    const sparks = Math.round(14 * look.sparkiness * scale);
    if (sparks > 0) {
      d = ps.begin(P_SPARK, true);
      d.count = sparks;
      d.px = x;
      d.py = y;
      d.pz = z;
      d.posJitter = 0.05;
      d.vx = nx * 8;
      d.vy = ny * 8;
      d.vz = nz * 8;
      d.spread = 1.15;
      d.speedVar = 0.75;
      d.velJitter = 2.2;
      d.gravity = -20;
      d.drag = 1.6;
      d.life = 0.5;
      d.lifeVar = 0.6;
      d.size0 = 0.15;
      d.size1 = 0.01;
      d.color0 = VFX.core;
      d.color1 = look.spark;
      d.intensity0 = 4;
      d.intensity1 = 0.9;
      d.bloom = 1;
      d.stretch = 1.3;
      d.priority = 2;
      ps.emit();
    }

    // --- solid debris
    const chips = Math.round(11 * look.chippiness * scale);
    if (chips > 0) {
      d = ps.begin(look.shape, false);
      d.count = chips;
      d.px = x;
      d.py = y;
      d.pz = z;
      d.posJitter = 0.08;
      d.vx = nx * 5.5;
      d.vy = ny * 5.5 + 1.4;
      d.vz = nz * 5.5;
      d.spread = 1.0;
      d.speedVar = 0.8;
      d.velJitter = 1.8;
      d.gravity = -24;
      d.drag = 0.4;
      d.life = 1.15;
      d.lifeVar = 0.5;
      d.size0 = 0.12 * scale;
      d.size1 = 0.09 * scale;
      d.color0 = look.debris;
      d.color1 = look.debris;
      d.spinRate = 9;
      d.spinVar = 9;
      d.priority = 1;
      ps.emit();
    }

    // --- dust
    if (look.dust !== 0) {
      d = ps.begin(P_PUFF, false);
      d.count = Math.round(7 * scale);
      d.px = x + nx * 0.1;
      d.py = y + ny * 0.1;
      d.pz = z + nz * 0.1;
      d.posJitter = 0.12;
      d.vx = nx * 2.6;
      d.vy = ny * 2.6 + 0.5;
      d.vz = nz * 2.6;
      d.spread = 1.0;
      d.speedVar = 0.6;
      d.gravity = 0.8;
      d.drag = 2.6;
      d.life = 0.85;
      d.lifeVar = 0.4;
      d.size0 = 0.3 * scale;
      d.size1 = 1.5 * scale;
      d.color0 = look.dust;
      d.color1 = VFX.ink;
      d.spinRate = 1.2;
      d.spinVar = 2;
      d.priority = 0;
      ps.emit();
    }

    // --- a few bright glints that outlive the flash, so the point of impact keeps
    //     drawing the eye for a beat after the light has gone.
    d = ps.begin(P_DOT, true);
    d.count = 5;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.16;
    d.vx = nx * 1.2;
    d.vy = ny * 1.2;
    d.vz = nz * 1.2;
    d.spread = 1.4;
    d.gravity = -3;
    d.drag = 3;
    d.life = 0.45;
    d.lifeVar = 0.5;
    d.size0 = 0.05;
    d.size1 = 0.0;
    d.color0 = VFX.core;
    d.color1 = look.spark;
    d.intensity0 = 3;
    d.intensity1 = 1;
    d.bloom = 1;
    d.priority = 1;
    ps.emit();
  }

  /**
   * A round striking a player's body. Ink, not gore — this is a comic, not a sim.
   *
   * A body shot is a WOUND, not a kill, and the effect says so: red, wet, low and
   * heavy, with none of the amber or the radiating spikes that belong to the headshot.
   * Nobody should ever have to check the killfeed to know which one just happened.
   */
  bodyHit(x: number, y: number, z: number, dx: number, dy: number, dz: number, victimTeam: number): void {
    const ps = this.particles;
    const team = TEAM_COLORS[victimTeam === 1 ? 1 : 0];

    let d = ps.begin(P_INK, false);
    d.count = 18;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.1;
    d.vx = dx * 6.5;
    d.vy = dy * 6.5 + 1.6;
    d.vz = dz * 6.5;
    d.spread = 1.3;
    d.speedVar = 0.8;
    d.velJitter = 2.4;
    d.gravity = -19;
    d.drag = 1.1;
    d.life = 0.8;
    d.lifeVar = 0.5;
    d.size0 = 0.16;
    d.size1 = 0.05;
    d.color0 = VFX.danger;
    d.color1 = VFX.dangerDeep;
    d.spinRate = 6;
    d.spinVar = 6;
    d.priority = 2;
    ps.emit();

    // Matte hit ring in the victim's colour — the confirmation the shooter reads, and
    // the thing a teammate across the arena sees. Opaque, so the white world cannot
    // swallow it.
    d = ps.begin(P_RING, false);
    d.count = 1;
    d.px = x - dx * 0.1;
    d.py = y - dy * 0.1;
    d.pz = z - dz * 0.1;
    d.life = 0.2;
    d.lifeVar = 0;
    d.size0 = 0.2;
    d.size1 = 1.5;
    d.sizeVar = 0;
    d.color0 = team;
    d.color1 = VFX.danger;
    d.drag = 5;
    d.priority = 3;
    ps.emit();

    const bodyK = this.solidScale(1.0, x, y, z);
    d = ps.begin(P_STAR, true);
    d.count = 1;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.life = 0.1;
    d.lifeVar = 0;
    d.size0 = 0.3 * bodyK;
    d.size1 = 1.0 * bodyK;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = VFX.danger;
    d.intensity0 = 5;
    d.intensity1 = 1.5;
    d.bloom = 1;
    d.spin = ps.random() * Math.PI;
    d.drag = 6;
    d.priority = 3;
    ps.emit();
  }

  /**
   * THE HEADSHOT. The peak moment of the entire game, and the one effect that is allowed
   * to be enormous — but it is over in a quarter of a second, and it is built out of
   * expanding rings and radiating spikes rather than a solid mass, so it never hides the
   * body it just took down or the enemy standing behind it.
   *
   * It is built matte-first, and that is what makes it land. The old version led with a
   * 4.6 m additive white star, which on a sunlit white wall is a slightly brighter white
   * wall: the biggest moment in the game rendered as almost nothing. Now the silhouette
   * is drawn — a hard amber star and three ink-edged shock rings — and the incandescent
   * pass sits inside it, one size smaller, purely to make it bloom.
   *
   * Amber, not team colour, for the star and the spikes: this is the round being spent,
   * and it is the one instant where the whole arena should see the chamber discharge.
   */
  headshot(x: number, y: number, z: number, dx: number, dy: number, dz: number, killerTeam: number): void {
    const ps = this.particles;
    const team = TEAM_COLORS[killerTeam === 1 ? 1 : 0];

    // Both stars are FILLED, so both are capped against how close the head was. A point
    // blank headshot keeps every other part of this effect at full size — the three shock
    // rings, the shards, the sparks, the ink — and those are hollow or granular, so the
    // moment stays enormous while the frame stays readable.
    const starK = this.solidScale(4.6, x, y, z);
    const lightK = this.solidScale(3.2, x, y, z);

    // --- THE SHAPE: a huge matte amber star that snaps open and is gone in four frames.
    let d = ps.begin(P_STAR, false);
    d.count = 1;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.life = 0.16;
    d.lifeVar = 0;
    d.size0 = 0.5 * starK;
    d.size1 = 4.6 * starK;
    d.sizeVar = 0;
    d.color0 = VFX.hot;
    d.color1 = VFX.hot;
    d.spin = 0.4;
    d.spinRate = 3;
    d.drag = 7;
    d.priority = 3;
    ps.emit();

    // --- the light inside it, deliberately smaller so the drawn star stays the shape.
    d = ps.begin(P_STAR, true);
    d.count = 1;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.life = 0.13;
    d.lifeVar = 0;
    d.size0 = 0.35 * lightK;
    d.size1 = 3.2 * lightK;
    d.sizeVar = 0;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 10;
    d.intensity1 = 2;
    d.bloom = 1;
    d.spin = 0.4;
    d.spinRate = 3;
    d.drag = 7;
    d.priority = 3;
    ps.emit();

    // --- three staggered shock rings, so the burst has rhythm instead of one bang.
    //     Matte and hollow: you can always see the fight straight through them.
    for (let i = 0; i < 3; i++) {
      d = ps.begin(P_RING, false);
      d.count = 1;
      d.px = x;
      d.py = y;
      d.pz = z;
      d.delay = i * 0.045;
      d.life = 0.34 - i * 0.05;
      d.lifeVar = 0;
      d.size0 = 0.3;
      d.size1 = 4.2 - i * 0.9;
      d.sizeVar = 0;
      d.color0 = i === 0 ? VFX.hot : team;
      d.color1 = team;
      d.drag = 3.4;
      d.priority = 3;
      ps.emit();
    }

    // --- radiating spikes: the comic "CRACK". Matte shards carry the silhouette,
    //     additive sparks ride along them and supply the sizzle.
    d = ps.begin(P_SHARD, false);
    d.count = 26;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.06;
    d.vx = -dx * 3;
    d.vy = -dy * 3 + 1.5;
    d.vz = -dz * 3;
    d.spread = Math.PI;
    d.speedVar = 0.2;
    d.velJitter = 13;
    d.gravity = -14;
    d.drag = 2.2;
    d.life = 0.46;
    d.lifeVar = 0.5;
    d.size0 = 0.26;
    d.size1 = 0.05;
    d.color0 = VFX.hot;
    d.color1 = VFX.ink;
    d.spinRate = 9;
    d.spinVar = 9;
    d.priority = 3;
    ps.emit();

    d = ps.begin(P_SPARK, true);
    d.count = 34;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.06;
    d.vx = -dx * 3;
    d.vy = -dy * 3 + 1.5;
    d.vz = -dz * 3;
    d.spread = Math.PI;
    d.speedVar = 0.2;
    d.velJitter = 13;
    d.gravity = -14;
    d.drag = 2.2;
    d.life = 0.42;
    d.lifeVar = 0.5;
    d.size0 = 0.3;
    d.size1 = 0.02;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 7;
    d.intensity1 = 1.6;
    d.bloom = 1;
    d.stretch = 1.9;
    d.priority = 3;
    ps.emit();

    // --- ink: a real splatter of team-coloured shapes that arcs and falls
    d = ps.begin(P_INK, false);
    d.count = 30;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.12;
    d.vx = -dx * 5;
    d.vy = -dy * 5 + 3.4;
    d.vz = -dz * 5;
    d.spread = 1.5;
    d.speedVar = 0.85;
    d.velJitter = 4.5;
    d.gravity = -22;
    d.drag = 0.8;
    d.life = 1.4;
    d.lifeVar = 0.5;
    d.size0 = 0.24;
    d.size1 = 0.07;
    d.color0 = team;
    d.color1 = VFX.ink;
    d.spinRate = 7;
    d.spinVar = 8;
    d.priority = 3;
    ps.emit();

    // --- and a slow drift of specks that lingers a beat after the bang, marking the
    //     spot for anyone who only caught the moment out of the corner of their eye.
    d = ps.begin(P_DOT, false);
    d.count = 24;
    d.px = x;
    d.py = y;
    d.pz = z;
    d.posJitter = 0.2;
    d.vx = 0;
    d.vy = 0.8;
    d.vz = 0;
    d.velJitter = 3.4;
    d.gravity = -1.5;
    d.drag = 2.4;
    d.life = 0.9;
    d.lifeVar = 0.5;
    d.delayVar = 0.08;
    d.size0 = 0.07;
    d.size1 = 0.02;
    d.color0 = VFX.hot;
    d.color1 = team;
    d.priority = 3;
    ps.emit();
  }

  // -------------------------------------------------------------------------
  // Generic marks
  // -------------------------------------------------------------------------

  /** Paint an ink splat on a surface. Used for blood on the floor and scorch marks. */
  splat(
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    size: number,
    body: number,
    hot: number,
    life = DECAL_LIFE,
  ): void {
    this.decals.place(
      x,
      y,
      z,
      nx,
      ny,
      nz,
      size,
      body,
      hot,
      this.particles.random(),
      this.time,
      life,
      this.particles.random() * Math.PI * 2,
    );
  }

  /**
   * Dust kicked off a surface, scaled by impact speed. Landings, gun bounces, dashes into
   * the floor. `speed01` is 0..1 of "how hard".
   */
  dustPuff(
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    speed01: number,
    surface: SurfaceKind,
  ): void {
    const ps = this.particles;
    const look = lookFor(surface);
    const s = speed01 < 0 ? 0 : speed01 > 1 ? 1 : speed01;
    const amount = 0.25 + s;

    let d = ps.begin(P_PUFF, false);
    d.count = Math.max(3, Math.round(9 * amount));
    d.px = x;
    d.py = y + 0.05;
    d.pz = z;
    d.posJitter = 0.22;
    d.vx = nx * 1.4;
    d.vy = ny * 1.4 + 0.4;
    d.vz = nz * 1.4;
    d.spread = 1.5;
    d.speedVar = 0.7;
    d.velJitter = 2.4 * amount;
    d.gravity = 0.7;
    d.drag = 3.0;
    d.life = 0.7;
    d.lifeVar = 0.4;
    d.size0 = 0.28 * amount;
    d.size1 = 1.5 * amount;
    d.color0 = look.dust !== 0 ? look.dust : VFX.dust;
    // Ambient dust settles to `dustDeep`, not to `ink`. This puff fires on every landing,
    // every slide, every wall-run and every gun bounce, and settling it to near-slate put
    // a constant drift of the darkest value in the palette through a high-key frame — the
    // loudest tone in the game, spent on the least informative event in the game. Struck
    // surfaces (worldImpact) still throw ink, so a SHOT's dust stays the dark one.
    d.color1 = VFX.dustDeep;
    d.spinRate = 1.4;
    d.spinVar = 2.2;
    d.priority = 0;
    ps.emit();

    if (look.sparkiness > 1 && s > 0.35) {
      d = ps.begin(P_SPARK, true);
      d.count = Math.round(8 * s * look.sparkiness);
      d.px = x;
      d.py = y + 0.04;
      d.pz = z;
      d.posJitter = 0.16;
      d.vx = nx * 4;
      d.vy = ny * 4;
      d.vz = nz * 4;
      d.spread = 1.4;
      d.speedVar = 0.7;
      d.velJitter = 3;
      d.gravity = -20;
      d.drag = 1.8;
      d.life = 0.35;
      d.lifeVar = 0.5;
      d.size0 = 0.11;
      d.size1 = 0.01;
      d.color0 = VFX.core;
      d.color1 = look.spark;
      d.intensity0 = 3.5;
      d.intensity1 = 0.9;
      d.bloom = 1;
      d.stretch = 1.2;
      d.priority = 1;
      ps.emit();
    }

    if (s > 0.5 && look.chippiness > 0.5) {
      d = ps.begin(look.shape, false);
      d.count = Math.round(6 * s * look.chippiness);
      d.px = x;
      d.py = y + 0.05;
      d.pz = z;
      d.posJitter = 0.18;
      d.vx = nx * 3;
      d.vy = ny * 3 + 1.2;
      d.vz = nz * 3;
      d.spread = 1.3;
      d.speedVar = 0.8;
      d.velJitter = 2.6;
      d.gravity = -24;
      d.drag = 0.5;
      d.life = 0.9;
      d.lifeVar = 0.5;
      d.size0 = 0.09;
      d.size1 = 0.07;
      d.color0 = look.debris;
      d.color1 = look.debris;
      d.spinRate = 8;
      d.spinVar = 8;
      d.priority = 0;
      ps.emit();
    }
  }

  // -------------------------------------------------------------------------

  update(dtSec: number): void {
    if (this.disposed) return;
    const dt = dtSec > 0.25 ? 0.25 : dtSec > 0 ? dtSec : 0;
    this.time += dt;
    this.decals.setTime(this.time);
    this.tracers.setTime(this.time);
    this.decals.flush();
    this.tracers.flush();
  }

  /** Wipe every mark. Called between rounds so a fresh round starts on a clean arena. */
  reset(): void {
    this.decals.clear();
    this.tracers.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.decals.mesh);
    this.scene.remove(this.tracers.mesh);
    this.scene.remove(this.tracers.glowMesh);
    this.decals.dispose();
    this.tracers.dispose();
  }
}
